/**
 * /home, user-mode Overview.
 *
 * Per Ean 2026-06-09 redesign: the previous Home was built before Chat
 * existed and treated MCP-via-Claude-Desktop as the primary consumption
 * path. That story is stale now. Chat is a first-class product surface,
 * the platform stands on three pillars (Models + Connectors + Prompts —
 * D-167 order), and external AI clients are one path among many — not
 * the headline.
 *
 * New layout:
 *
 *   1. Greeting card (slim)                — name + workspace pulse
 *   2. Quick start card                    — "Chat with your AI" primary CTA,
 *                                            mentions what's currently
 *                                            assigned so the value-prop is
 *                                            concrete, not abstract
 *   3. Your toolkit (3-col)                — Models / Connectors / Prompts
 *                                            (D-167 order) at-a-glance with
 *                                            counts + entry links;
 *                                            empty-state aware per pillar
 *   4. For power users (collapsed)         — Use Olytic from your favorite AI
 *                                            client (Claude Desktop, ChatGPT,
 *                                            Cursor, etc.); MCP URL surface,
 *                                            collapsed by default so Lori
 *                                            isn't intimidated. When expanded,
 *                                            walks through what an MCP URL
 *                                            is, where to paste per client,
 *                                            and a first-prompt nudge.
 *   5. Recent activity                     — always-last per Ean 2026-06-10
 *                                            (activity ranks below the
 *                                            teach-me-more section both
 *                                            vertically and horizontally
 *                                            across the app).
 *
 * Audience reminder: Lori — non-technical lead. The page leads with what
 * she can DO (chat), tells her what she HAS (three-pillar toolkit), and
 * keeps the technical surfaces (MCP URLs, external clients) collapsed and
 * labeled as power-user paths.
 *
 * Standard users see NO admin affordances. Email verification banner sits
 * above everything if email_verified=false.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatedNumber } from '../components/AnimatedNumber.tsx';
import { BrandLogo } from '../components/BrandLogo.tsx';
import { BuyCreditsButton } from '../components/BuyCreditsButton.tsx';
import { CopyButton } from '../components/CopyButton.tsx';
import { EmptyState } from '../components/EmptyState.tsx';
import { IconChevron, IconCoin } from '../components/Icon.tsx';
// Phase 1.21 (2026-06-29): EmailVerificationBanner moved to UserShell —
// renders persistently across every authenticated route. Import dropped
// here since the 3 prior Home.tsx mounts were removed.
// import { EmailVerificationBanner } from '../components/EmailVerificationBanner.tsx';
import { ObjectIcon, type ObjectKind } from '../components/ObjectIcon.tsx';
import { RecordLink } from '../components/RecordLink.tsx';
import { SkeletonBlock } from '../components/Skeleton.tsx';
import { DispatchStatusPill } from '../components/StatusPill.tsx';
import { useDelayedFlag } from '../hooks/useDelayedFlag.ts';
import { useSession } from '../hooks/useSession.tsx';
import {
  ApiError,
  type DashboardData,
  fetchMyConnectors,
  fetchMyDashboard,
  fetchMyKnowledgePrompts,
  fetchMyModels,
  type KnowledgePromptInstance,
  type LlmModelInstance,
  type MyConnectorDetailed,
} from '../lib/api.ts';

const MCP_URL = 'https://workspace.olytic.com/mcp';

export function Home() {
  const { user } = useSession();
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [connectors, setConnectors] = useState<MyConnectorDetailed[] | null>(null);
  const [knowledge, setKnowledge] = useState<KnowledgePromptInstance[] | null>(null);
  const [models, setModels] = useState<LlmModelInstance[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Push #15 → Push #19 walkback: the "Use Olytic in your favorite AI app"
  // section is collapsed by default per Ean 2026-06-10. Lori's primary path
  // is Chat → the in-app surface. External-app setup is a power-user side
  // door that should be accessible but not visually dominant. State persists
  // to localStorage so a user who opens it stays opened across reloads.
  const [connectAppOpen, setConnectAppOpen] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem('home:connect-app-open') === '1';
    } catch {
      return false;
    }
  });
  const [selectedApp, setSelectedApp] = useState<'claude' | 'chatgpt' | 'cursor' | 'other'>(
    'claude',
  );

  // Skeleton flag — only true if loading takes >200ms. Fast loads never flash.
  const loading =
    dashboard === null || connectors === null || knowledge === null || models === null;
  const showSkeleton = useDelayedFlag(loading);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      fetchMyDashboard(),
      fetchMyConnectors(),
      fetchMyKnowledgePrompts(),
      fetchMyModels(),
    ])
      .then(([d, c, k, m]) => {
        if (cancelled) return;
        setDashboard(d);
        setConnectors(c.connectors);
        setKnowledge(k.prompts);
        setModels([...(m.default !== null ? [m.default] : []), ...m.alternatives]);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : 'Could not load your workspace.');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (user === null) return null;

  if (error !== null) {
    // Phase 1.21 (2026-06-29): EmailVerificationBanner mounted in
    // UserShell — no longer needed at every Home.tsx render branch.
    return (
      <div className="callout callout-danger">
        <p className="callout-title">Couldn't load your workspace</p>
        <p className="callout-body">{error}</p>
      </div>
    );
  }

  if (loading) {
    return showSkeleton ? <HomeSkeleton /> : <div style={{ minHeight: '40vh' }} />;
  }

  const firstName = user.name.split(' ')[0] ?? user.name;
  const hour = new Date().getHours();
  const greeting =
    hour < 5 ? 'Hi' : hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';

  const connectorCount = connectors.length;
  const knowledgeCount = knowledge.length;
  const modelCount = models.length;
  const defaultModel = models.find((m) => m.is_workspace_default) ?? models[0] ?? null;

  // Quick-start value-prop string assembles based on what the user actually
  // has, so it reads concrete instead of marketing-y. Examples:
  //   "You have 2 connectors active, 5 prompts ready, and Claude Haiku 4.5 on call."
  //   "You have Claude Haiku 4.5 ready. Add a connector to give it real tools."
  const valueProp = buildValueProp(connectorCount, knowledgeCount, defaultModel);

  return (
    <div>
      {/* Phase 1.21 (2026-06-29): EmailVerificationBanner promoted to
          UserShell. renders persistently across every authenticated
          route. The Home.tsx mount that lived here is no longer needed. */}

      {/* 1. Greeting (slim) ============================================== */}
      <div className="lightning-highlight">
        <div className="lightning-highlight-left">
          <ObjectIcon kind="home" size="lg" />
          <div className="lightning-highlight-text">
            <h1 className="lightning-title">
              {greeting}, {firstName}.
            </h1>
            <p
              className="muted"
              style={{ margin: '6px 0 0', fontSize: '13px', maxWidth: '72ch', lineHeight: 1.45 }}
            >
              {valueProp}
            </p>
          </div>
        </div>
        <div className="lightning-highlight-actions">
          <Link to="/chat" className="btn btn-primary">
            Start chatting
          </Link>
        </div>
      </div>

      {/* 2. Your toolkit at a glance ===================================
         Push 5-post-smoke-40 (2026-07-15): toolkit promoted from position 3
         to position 2 per Ean. The three peer surfaces below (credits,
         external-AI-app setup, recent activity) now sit as a combined row
         so Home reads as: greeting → what you have → the peripheral stuff
         (top-up, power-user side door, recent runs) in one visual band. */}
      <section className="card card-padded" style={{ marginBottom: 'var(--card-gap)' }}>
        {/* Section-level actions are buttons per Ean 2026-06-09. arrows are
           reserved for card-level affordances (the "View all →" CTA inside
           each PillarTile is the right place for an arrow). "Browse market"
           is a section-level navigation, so it gets real button chrome. */}
        <div className="section-row">
          <h2 className="section-title" style={{ marginTop: 0 }}>
            Your toolkit
          </h2>
          <Link to="/market" className="btn btn-secondary btn-sm">
            Browse market
          </Link>
        </div>
        <p
          className="muted"
          style={{ margin: '4px 0 16px', fontSize: '13px', lineHeight: 1.45, maxWidth: '72ch' }}
        >
          Everything your AI can use on your behalf. Connectors to act on, prompts to start from,
          and models to think with. All assigned by your admin.
        </p>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: '12px',
          }}
        >
          {/* D-167: Models → Connectors → Prompts */}
          <PillarTile
            iconKind="models"
            title="Models"
            count={modelCount}
            blurb={
              defaultModel === null
                ? 'AI brains assigned to you. Pick the right one for the task.'
                : `${defaultModel.display_name} is your default. Switch any time from the chat picker.`
            }
            emptyHint="Ask your admin to assign you a model."
            to="/models"
            cta="View all →"
          />
          <PillarTile
            iconKind="connectors"
            title="Connectors"
            count={connectorCount}
            blurb="Tools your AI can act on for you. Search, send, update, summarize."
            emptyHint="Browse the market and request the connectors you need."
            to="/connectors"
            cta="View all →"
          />
          <PillarTile
            iconKind="prompts"
            title="Prompts"
            count={knowledgeCount}
            blurb="Ready-made instructions for Olytic chat or any MCP-aware AI client. Saves you typing the same thing twice."
            emptyHint="Create your own prompt to jump-start chats."
            to="/prompts"
            cta="View all →"
          />
          {/* Push 4-1c-e (2026-07-01) — Library pillar surfaced per
             LOCKED MENTAL MODEL. Count wires up when Phase 4E lands
             the ingestion pipeline; empty-state carries value prop
             for now. */}
          <PillarTile
            iconKind="library"
            title="Library"
            count={0}
            blurb="Reference documents chat can pull answers from. Upload once, cite everywhere."
            emptyHint="Upload a document to start your library."
            to="/library"
            cta="View all →"
          />
        </div>
      </section>

      {/* Stacked full-width band per Push 5-post-smoke-42 (2026-07-15) —
         Ean walked back Push 40's 3-column grid. Toolkit already above.
         Order: Credits → Use Olytic in favorite AI app → Recent activity.
         Each is its own full-width card stacked vertically. */}
      {/* Credits (Push 4-1ah, 2026-07-10) — small wallet widget so users
         can top up (or request a top-up) without hunting through
         Settings > Profile > Credits. Ean's frame: many ways to top off
         and spend money on credits proactively. */}
      <YourCreditsWidget />

      {/* Use Olytic in your favorite AI app (Push #15 + Push #19) —
         power-user side door for Claude Desktop / ChatGPT / Cursor.
         Collapsed-by-default per Ean 2026-06-10 because Chat IS Lori's
         primary path; external-app setup is accessible but not visually
         dominant. Open-state persists to localStorage.
         Voice rules locked: never "MCP URL" (always "your Olytic link"),
         never "MCP-aware AI" (always "AI apps that support custom
         connections"). */}
      <ConnectAppSection
        mcpUrl={MCP_URL}
        selectedApp={selectedApp}
        onSelectApp={setSelectedApp}
        open={connectAppOpen}
        onToggle={(v) => {
          setConnectAppOpen(v);
          try {
            window.localStorage.setItem('home:connect-app-open', v ? '1' : '0');
          } catch {
            /* storage unavailable — accept */
          }
        }}
      />

      {/* Recent activity (Push #19 + Push 4-1d-s) — always-last per Ean
         2026-06-10. Full-width band, dashboard endpoint caps at 5 rows;
         "See all activity" drills to /activity.
         Push #25 header pattern (ObjectIcon left of title). */}
      <section className="card card-padded">
        {/* Push #25. icon + title sit together. The default .section-row uses
              space-between which was pushing the title to the far right; override
              with flex-start so they hug each other left-aligned. */}
        <div
          className="section-row"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            gap: '10px',
          }}
        >
          <ObjectIcon kind="activity" size="md" />
          <h2 className="section-title" style={{ marginTop: 0, marginBottom: 0 }}>
            Recent activity
          </h2>
        </div>
        {dashboard.recent_activity.length === 0 ? (
          <EmptyState
            kind="activity"
            title="No activity yet"
            body="Once you chat with your AI or it acts on one of your connectors, recent runs show up here."
          />
        ) : (
          <div className="activity-list">
            {/* Connector name is a RecordLink per Ean 2026-06-09. clicking it
                 drills to /activity/:short_id (the foundation-level activity
                 record). Hover shows dotted underline + teal tint. The whole
                 row stays passive; only the name is the click target so power
                 users get drill-down without making the row read as a button.
                 Push 4-1d-k-cont Class B (2026-07-02): switched from `.id`
                 (UUID) to `.short_id` (evt-prefix) so the URL matches the
                 short_id-shaped /activity/:short_id route. Endpoint extended
                 to return sys__event_log.short_id in the same push. */}
            {dashboard.recent_activity.map((a) => (
              <div key={a.id} className="activity-row">
                <div>
                  <div className="activity-primary">
                    <RecordLink to={`/activity/${a.short_id}`} title="Open this activity record">
                      {a.connector_name}
                    </RecordLink>
                  </div>
                  <div className="activity-sub">{relativeTime(a.created_at)}</div>
                </div>
                <DispatchStatusPill status={a.status} />
                <div className="activity-meta">
                  {a.duration_ms !== null ? `${a.duration_ms}ms` : '-'}
                </div>
                <div />
              </div>
            ))}
          </div>
        )}
        {/* Push 4-1d-s Item 2 (2026-07-06) — "See all activity" drill-in to
             /activity (user-scoped list surface). Only surfaces when there IS
             recent activity; empty-state carries its own copy. Kept as a small
             right-aligned link so it reads as an affordance, not a CTA — the
             Home surface leads with Connect / Chat, not with drill-down. */}
        {/* Push 4-1u (2026-07-09): capped size to 12px muted per Ean nitpick —
             "See all activity" was reading ~2x bigger than sibling muted text
             because RecordLink defaulted to inherit body font-size (~15px).
             Now scales with the small-muted rhythm; still teal on hover as
             an affordance signal. */}
        {dashboard.recent_activity.length > 0 && (
          <div
            style={{
              marginTop: '12px',
              textAlign: 'right',
              fontSize: 12,
              color: 'var(--fg-muted)',
            }}
          >
            <RecordLink to="/activity" size="sm" muted>
              See all activity
            </RecordLink>
          </div>
        )}
      </section>

      {/* Trust whisper. closes the page with a quiet reinforcement of the
         value-prop per Ean 2026-06-09 "do this more everywhere". Same voice
         as the chat rail whisper, plain-English, no security jargon. */}
      <div className="trust-whisper trust-whisper--footer">
        Your AI only acts on what your admin allowed · Every step is recorded
      </div>
    </div>
  );
}

/**
 * Push #15 — "Use Olytic in your favorite AI app" section.
 *
 * Replaces the prior "MCP URL" power-user card. Job-to-be-done: get a
 * non-technical user from "what is this" to "it works" in under 60 seconds.
 * Three steps, three cards, no body text under step headers — let the
 * structure carry the meaning.
 *
 *   Step 1 — Copy your Olytic link              (URL + Copy button)
 *   Step 2 — Pick your AI app                   (tile selector with inline reveal)
 *   Step 3 — Check it worked                    (first-prompt nudge)
 *
 * Voice locked: never "MCP URL", always "your Olytic link". The word "MCP"
 * appears only inside the exact settings paths a user will read in another
 * app's UI (e.g., ChatGPT's actual button is "Add MCP server" — so we keep
 * that quoted as-is, framed as "what to click in your AI app").
 */
type ConnectAppKind = 'claude' | 'chatgpt' | 'cursor' | 'other';

interface ConnectAppEntry {
  kind: ConnectAppKind;
  /** Display label shown on the tile. */
  label: string;
  /** Brand slug — registered in BrandLogo.tsx (claude/chatgpt/cursor) or
   *  falls back to the AppIcon initial tile for unknown clients. */
  slug: string;
  /** Per-app step lines. Each line is one click in the target app's UI.
   *  We quote settings paths exactly as the user will see them — no dev-doc
   *  arrows or backticks. */
  steps: string[];
}

const CONNECT_APPS: ConnectAppEntry[] = [
  {
    kind: 'claude',
    label: 'Claude Desktop',
    slug: 'claude',
    steps: [
      'Open Claude Desktop',
      'Go to Settings → Connectors',
      'Click "Add custom connector"',
      'Paste your Olytic link',
      'Sign in to Olytic when prompted',
    ],
  },
  {
    kind: 'chatgpt',
    label: 'ChatGPT',
    slug: 'chatgpt',
    steps: [
      'Open ChatGPT',
      'Go to Settings → Connectors',
      'Click "Add MCP server"',
      'Paste your Olytic link',
      'Sign in to Olytic when prompted',
    ],
  },
  {
    kind: 'cursor',
    label: 'Cursor',
    slug: 'cursor',
    steps: [
      'Open Cursor',
      'Go to Settings → Tools & MCP',
      'Click "Add new MCP server"',
      'Paste your Olytic link',
      'Sign in to Olytic when prompted',
    ],
  },
  {
    kind: 'other',
    label: 'Other',
    slug: 'other',
    steps: [
      'Open your AI app’s settings',
      'Look for an "MCP server" or "custom connector" field',
      'Paste your Olytic link there',
      'Sign in to Olytic when prompted',
    ],
  },
];

function ConnectAppSection({
  mcpUrl,
  selectedApp,
  onSelectApp,
  open,
  onToggle,
}: {
  mcpUrl: string;
  selectedApp: ConnectAppKind;
  onSelectApp: (k: ConnectAppKind) => void;
  open: boolean;
  onToggle: (v: boolean) => void;
}) {
  const active = CONNECT_APPS.find((a) => a.kind === selectedApp) ?? CONNECT_APPS[0]!;

  return (
    <section className="lightning-section" style={{ marginBottom: 'var(--card-gap)' }}>
      {/* Section header. clickable row that flips the collapse state per
         Push #19. Whole row is the toggle target (chevron + title both
         hot); the body below renders only when open=true. */}
      <button
        type="button"
        onClick={() => onToggle(!open)}
        aria-expanded={open}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          width: '100%',
          background: 'transparent',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
          fontFamily: 'inherit',
          textAlign: 'left',
          marginBottom: open ? '4px' : 0,
        }}
      >
        <div style={{ minWidth: 0, flex: 1 }}>
          <h2
            className="lightning-section-title"
            style={{ marginTop: 0, marginBottom: open ? '4px' : 0 }}
          >
            Use Olytic in your favorite AI app
          </h2>
          {!open && (
            <p className="muted" style={{ margin: 0, fontSize: '13px', lineHeight: 1.45 }}>
              Connect once, then say <strong>"Hey Olytic"</strong> from Claude Desktop, ChatGPT, or
              Cursor.
            </p>
          )}
        </div>
        {/* Push 5-post-smoke-25a (2026-07-14) — inline chevron SVG
            replaced with IconChevron primitive. Same currentColor +
            rotation discipline; snaps to the canonical chevron
            geometry the rest of the app uses. Transition preserved
            via style pass-through. */}
        <span
          aria-hidden="true"
          style={{
            flexShrink: 0,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--fg-muted)',
          }}
        >
          <IconChevron
            direction={open ? 'down' : 'right'}
            size={12}
            style={{ transition: 'transform 140ms ease' }}
          />
        </span>
      </button>
      {open && (
        <>
          <p
            className="muted"
            style={{
              margin: '0 0 var(--sp-4)',
              fontSize: '13px',
              lineHeight: 1.5,
              maxWidth: '72ch',
            }}
          >
            Connect once. Then any time you'd say "Hey Alexa," say <strong>"Hey Olytic"</strong>{' '}
            instead. Your AI knows to use your tools.
          </p>

          {/* Step 1. Copy your Olytic link */}
          <div style={{ marginBottom: 'var(--sp-4)' }}>
            <StepHeader num={1} label="Copy your link" />
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--sp-2)',
                padding: 'var(--sp-2) var(--sp-3)',
                background: 'var(--olytic-surface)',
                border: '1px solid var(--border)',
                borderRadius: '8px',
              }}
            >
              <code
                style={{
                  flex: 1,
                  fontFamily: 'var(--font-mono)',
                  fontSize: '12.5px',
                  color: 'var(--olytic-obsidian)',
                  overflow: 'auto',
                  whiteSpace: 'nowrap',
                }}
              >
                {mcpUrl}
              </code>
              <CopyButton
                value={mcpUrl}
                successMessage="Your Olytic link is copied. Paste it into your AI app."
              >
                Copy link
              </CopyButton>
            </div>
          </div>

          {/* Step 2. Pick your AI app (tile selector with inline reveal) */}
          <div style={{ marginBottom: 'var(--sp-4)' }}>
            <StepHeader num={2} label="Pick your AI app" />
            <div
              role="tablist"
              aria-label="AI apps"
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                gap: 'var(--sp-2)',
                marginBottom: 'var(--sp-3)',
              }}
            >
              {CONNECT_APPS.map((app) => {
                const isActive = app.kind === selectedApp;
                return (
                  <button
                    key={app.kind}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => onSelectApp(app.kind)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '10px 12px',
                      background: isActive
                        ? 'color-mix(in srgb, var(--olytic-teal) 6%, transparent)'
                        : 'var(--olytic-white)',
                      border: isActive ? '1px solid var(--olytic-teal)' : '1px solid var(--border)',
                      borderRadius: '8px',
                      borderLeft: isActive
                        ? '3px solid var(--olytic-teal)'
                        : '3px solid transparent',
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                      textAlign: 'left',
                      transition: 'background 140ms ease, border-color 140ms ease',
                    }}
                  >
                    <BrandLogo slug={app.slug} name={app.label} size="sm" />
                    <span
                      style={{
                        fontSize: '13px',
                        fontWeight: isActive ? 600 : 500,
                        color: isActive ? 'var(--olytic-teal)' : 'var(--olytic-obsidian)',
                      }}
                    >
                      {app.label}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Inline per-app steps. revealed by the selected tile above. */}
            <div
              style={{
                padding: '14px 16px',
                background: 'var(--olytic-white)',
                border: '1px solid var(--border)',
                borderRadius: '8px',
              }}
            >
              <div
                style={{
                  fontSize: '12px',
                  fontWeight: 600,
                  color: 'var(--olytic-obsidian)',
                  marginBottom: '10px',
                }}
              >
                In {active.label}:
              </div>
              <ol
                style={{
                  margin: 0,
                  paddingLeft: '20px',
                  fontSize: '13px',
                  color: 'var(--fg)',
                  lineHeight: 1.6,
                }}
              >
                {active.steps.map((s, i) => (
                  <li key={i} style={{ marginBottom: '2px' }}>
                    {s}
                  </li>
                ))}
              </ol>
            </div>
          </div>

          {/* Step 3. Try it */}
          <div>
            <StepHeader num={3} label="Try it" />
            <div
              style={{
                padding: 'var(--sp-3) var(--sp-4)',
                background: 'var(--olytic-surface)',
                border: '1px solid var(--border)',
                borderRadius: '8px',
              }}
            >
              <p
                style={{
                  margin: 0,
                  fontStyle: 'italic',
                  fontSize: '14px',
                  color: 'var(--olytic-teal)',
                  fontWeight: 500,
                }}
              >
                "Hey Olytic, what can you do?"
              </p>
              <p
                style={{
                  margin: 'var(--sp-2) 0 0',
                  fontSize: '12.5px',
                  color: 'var(--fg-muted)',
                  lineHeight: 1.55,
                }}
              >
                It'll list every connector, prompt, and model your admin gave you. From there, lead
                any request with <strong>"Hey Olytic"</strong> and it'll route through your
                governance layer.
              </p>
            </div>
          </div>
        </>
      )}
    </section>
  );
}

/** Compact step header — Round 15 refresh. Smaller circle, tighter label,
 *  drops the redundant "Step N —" prefix (the circle already carries the
 *  number) for a Series B+ aesthetic that matches Linear / Anthropic Console. */
function StepHeader({ num, label }: { num: number; label: string }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--sp-2)',
        marginBottom: 'var(--sp-2)',
      }}
    >
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '18px',
          height: '18px',
          borderRadius: '50%',
          background: 'var(--olytic-teal)',
          color: 'var(--olytic-white)',
          fontSize: '11px',
          fontWeight: 700,
          flexShrink: 0,
          lineHeight: 1,
        }}
        aria-hidden="true"
      >
        {num}
      </span>
      <span
        style={{
          fontSize: '13.5px',
          fontWeight: 600,
          color: 'var(--olytic-obsidian)',
          letterSpacing: '-0.005em',
        }}
      >
        {label}
      </span>
    </div>
  );
}

/**
 * One column in the "Your toolkit" three-up grid. Same shape for all three
 * pillars. Count is prominent; blurb gives Lori the why; empty-state hint
 * replaces the blurb when count is zero. Whole card is clickable.
 */
function PillarTile({
  iconKind,
  title,
  count,
  blurb,
  emptyHint,
  to,
  cta,
}: {
  iconKind?: ObjectKind;
  title: string;
  count: number;
  blurb: string;
  emptyHint: string;
  to: string;
  cta: string;
}) {
  const isEmpty = count === 0;
  return (
    <Link
      to={to}
      className="card card-clickable"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        padding: '16px',
        borderRadius: '10px',
        minHeight: '140px',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '8px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {iconKind !== undefined && <ObjectIcon kind={iconKind} size="sm" />}
          <span
            style={{
              fontSize: '12px',
              fontWeight: 600,
              color: 'var(--fg-muted)',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
            }}
          >
            {title}
          </span>
        </div>
        <span
          style={{
            fontSize: '24px',
            fontWeight: 700,
            color: isEmpty ? 'var(--fg-muted)' : 'var(--olytic-obsidian)',
            lineHeight: 1,
          }}
        >
          {count}
        </span>
      </div>
      <p
        style={{
          margin: 0,
          fontSize: '13px',
          color: 'var(--fg-muted)',
          lineHeight: 1.45,
          flex: 1,
        }}
      >
        {isEmpty ? emptyHint : blurb}
      </p>
      <span className="btn-link" style={{ fontSize: '13px', padding: 0, alignSelf: 'flex-start' }}>
        {cta}
      </span>
    </Link>
  );
}

/**
 * Concrete one-liner derived from the user's actual assignments. Keeps the
 * greeting card's subtitle grounded in real state instead of generic
 * marketing copy.
 */
function buildValueProp(
  connectorCount: number,
  knowledgeCount: number,
  defaultModel: LlmModelInstance | null,
): string {
  const modelLabel = defaultModel === null ? null : defaultModel.display_name;
  if (connectorCount === 0 && knowledgeCount === 0 && modelLabel === null) {
    return 'Welcome to Olytic. Once your admin assigns you a model, you can start chatting right away.';
  }
  if (connectorCount === 0 && modelLabel !== null) {
    return `You can chat with ${modelLabel} now. Add a connector to give your AI real tools to act on.`;
  }
  const parts: string[] = [];
  if (connectorCount > 0) {
    parts.push(`${connectorCount} connector${connectorCount === 1 ? '' : 's'} active`);
  }
  if (knowledgeCount > 0) {
    parts.push(`${knowledgeCount} prompt${knowledgeCount === 1 ? '' : 's'} ready`);
  }
  if (modelLabel !== null) {
    parts.push(`${modelLabel} on call`);
  }
  return `You have ${joinHumanList(parts)}.`;
}

function joinHumanList(parts: string[]): string {
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0]!;
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
  return `${parts.slice(0, -1).join(', ')}, and ${parts[parts.length - 1]}`;
}

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = Math.max(0, now - then);
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

/**
 * YourCreditsWidget — compact wallet card on user home.
 *
 * Push 70b (D-198 wallet collapse, 2026-07-17): wallet_policy branch
 * retired. Every user reads the single workspace credit pot; the widget
 * surfaces the balance + a subtle caption. Personal spend cap (if set)
 * flows through as a subtitle so users know when they're on a leash.
 */
function YourCreditsWidget() {
  const [wallet, setWallet] = useState<{
    accountBal: number;
    monthlyCapCredits: number | null;
    spentThisCycleCredits: number;
  } | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const resp = await fetch('/api/me/wallet', { credentials: 'include' });
        if (!resp.ok) return;
        const data = (await resp.json()) as {
          account?: { token_balance_cached_credits?: string };
          user?: {
            monthly_cap_credits?: string | null;
            spend_this_month_credits?: string;
          };
        };
        if (cancelled) return;
        const accountBal = Number(data.account?.token_balance_cached_credits ?? 0);
        const capRaw = data.user?.monthly_cap_credits;
        const cap = capRaw === null || capRaw === undefined ? null : Number(capRaw);
        const spent = Number(data.user?.spend_this_month_credits ?? 0);
        setWallet({
          accountBal: Number.isFinite(accountBal) ? accountBal : 0,
          monthlyCapCredits: cap !== null && Number.isFinite(cap) ? cap : null,
          spentThisCycleCredits: Number.isFinite(spent) ? spent : 0,
        });
      } catch {
        /* silent */
      }
    }
    void load();
    function onChanged() {
      void load();
    }
    window.addEventListener('olytic:wallet-changed', onChanged);
    return () => {
      cancelled = true;
      window.removeEventListener('olytic:wallet-changed', onChanged);
    };
  }, []);

  if (wallet === null) return null;

  const displayBalance = wallet.accountBal;
  const policyCaption =
    wallet.monthlyCapCredits !== null
      ? `Your spend limit: ${Math.max(0, wallet.monthlyCapCredits - wallet.spentThisCycleCredits).toLocaleString()} credits left`
      : 'Shared workspace credits';

  return (
    <section className="card card-padded" style={{ marginBottom: 'var(--card-gap)' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, minWidth: 0 }}>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 40,
              height: 40,
              borderRadius: 10,
              background: 'color-mix(in srgb, var(--olytic-teal) 10%, transparent)',
              color: 'var(--olytic-teal)',
              flexShrink: 0,
            }}
            aria-hidden="true"
          >
            <IconCoin size={18} />
          </span>
          <div style={{ minWidth: 0 }}>
            <div className="muted" style={{ fontSize: 11, marginBottom: 2 }}>
              Your credits
            </div>
            <div
              style={{
                fontSize: 20,
                fontWeight: 600,
                fontVariantNumeric: 'tabular-nums',
                lineHeight: 1.1,
              }}
            >
              <AnimatedNumber
                to={displayBalance}
                format={(n) =>
                  Math.round(n * 100) / 100 === Math.round(n)
                    ? Math.round(n).toLocaleString()
                    : n.toLocaleString(undefined, { maximumFractionDigits: 2 })
                }
              />{' '}
              <span className="muted" style={{ fontSize: 12, fontWeight: 400 }}>
                credits · {policyCaption}
              </span>
            </div>
          </div>
        </div>
        <BuyCreditsButton size="sm" variant="secondary" reason="proactive" />
      </div>
    </section>
  );
}

/**
 * HomeSkeleton — placeholder mirroring the Home layout (greeting bar,
 * toolkit row, activity card). Same shape as the real page so the
 * skeleton reads as the page becoming.
 */
function HomeSkeleton() {
  return (
    <div>
      <SkeletonBlock height={96} radius={12} />
      <div style={{ height: 'var(--card-gap)' }} />
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 12,
        }}
      >
        <SkeletonBlock height={140} />
        <SkeletonBlock height={140} />
        <SkeletonBlock height={140} />
      </div>
      <div style={{ height: 'var(--card-gap)' }} />
      <SkeletonBlock height={160} />
    </div>
  );
}
